import PropTypes from 'prop-types';

import Downshift from 'downshift';
import { withStyles } from 'legacyStyles';

import TextField from 'Components/TextField';

import AutoCompleteItem from './AutoCompleteItem';
import AutoCompleteOptions from './AutoCompleteOptions';

const styles = {
  root: {
    position: 'relative',
  },
};

/**
 * This is the standard AutoComplete component
 * - suggestions are an array of strings
 * - filter suggestions using case insensitive text regexp
 * - open suggestion list on input focus
 * - cancel input if no option is selected
 */

const AutoComplete = ({
  id,
  label,
  error,
  classes,
  suggestions,
  value,
  onBlur,
  onChange,
  textFieldProps,
  ...props
}) => (
  <Downshift onSelect={onChange} selectedItem={value} {...props}>
    {({
      getInputProps,
      getItemProps,
      highlightedIndex,
      inputValue,
      isOpen,
      openMenu,
      selectedItem,
    }) => {
      const inputRegExp = new RegExp(inputValue, 'i');

      return (
        <div className={classes.root}>
          <TextField
            {...getInputProps({ id, onBlur, onFocus: openMenu })}
            error={error}
            label={label}
            {...textFieldProps}
          />
          {isOpen && (
            <AutoCompleteOptions>
              {suggestions
                .filter(suggestion => inputRegExp.test(suggestion))
                .map((suggestion, index) => (
                  <AutoCompleteItem
                    key={index}
                    {...getItemProps({
                      item: suggestion,
                      isHighlighted: highlightedIndex === index,
                      isSelected: selectedItem === suggestion,
                    })}
                    selected={index === highlightedIndex}
                  >
                    {suggestion}
                  </AutoCompleteItem>
                ))}
            </AutoCompleteOptions>
          )}
        </div>
      );
    }}
  </Downshift>
);

AutoComplete.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  // The error message for the `TextField` element
  error: PropTypes.string,
  // The id of the `TextField` element
  id: PropTypes.string,
  // The label of the `TextField` element
  label: PropTypes.node,
  // The onBlur callback of the 'TextField' element
  onBlur: PropTypes.func,
  // The callback when an option is selected
  onChange: PropTypes.func,
  // The suggested options to list
  suggestions: PropTypes.arrayOf(PropTypes.string).isRequired,
  // The props to be passed directly to the TextField component
  textFieldProps: PropTypes.shape({}),
  // The initial value to prepopulate the state
  value: PropTypes.string,
};

export const withAutoCompleteStyles = withStyles(styles);

export default withAutoCompleteStyles(AutoComplete);
